import React, { useState } from 'react';
import { Alert, Input, Button, Spin, Form } from 'antd';
import { prop, isNil, propOr } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import withUser from '../../withUser';
import { passwordIsValid } from '../../lib/validation';
import useActionGuard from '../../hooks/useActionGuard';
import ResetPasswordMutation from '../../_graphql/mutations/ResetPasswordMutation';
import SignOutMutation from '../../_graphql/mutations/SignOutMutation';

const useNewPasswordRequested = (history, form, user) => {
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState(null);
  const { loading, run } = useActionGuard();

  const hasErrors = () => {
    const fieldsError = form.getFieldsError();
    return Object.keys(fieldsError).some(field => {
      return fieldsError[field] || isNilOrEmpty(form.getFieldValue(field));
    });
  };

  const onLostCurrentPassword = () => {
    run(`sign-out-${prop('email', user)}`, (done) => {
      SignOutMutation(() => {
        done();
        history.replace('/account/resetpasswordrequest');
      });
    });
  };
  const onUpdatePassword = (e) => {
    e.preventDefault();
    const { currentPassword, password } = form.getFieldsValue();
    run(`update-password-${prop('email', user)}`, (done) => {
      ResetPasswordMutation({
        email: prop('email', user), currentPassword, password
      }, (ok, error) => {
        if (ok) setIsDone(true);
        else setError(error);
        done(ok, error);
      });
    });
  };

  const onResetForm = () => {
    setIsDone(false);
    setError(null);
  };

  return {
    hasErrors,
    onLostCurrentPassword,
    onResetForm,
    onUpdatePassword,
    passwordIsUpdated: isDone,
    processing: propOr(false, `update-password-${prop('email', user)}`, loading),
    error
  };
};

const render = (content) => (
  <div className="connection-box">
    {content}
  </div>
);

const NewPasswordRequired = ({
  history,
  user
}) => {
  const [form] = Form.useForm();

  const {
    processing,
    onResetForm,
    onLostCurrentPassword,
    onUpdatePassword,
    passwordIsUpdated,
    error,
    hasErrors
  } = useNewPasswordRequested(history, form, user);

  if (processing) {
    return render(
      <React.Fragment>
        <Spin/>
        &nbsp;
        <span>Nous mettons à jour votre mot de passe...</span>
      </React.Fragment>

    );
  } else if (!processing && isNil(error) && !passwordIsUpdated) {
    return render(
      <React.Fragment>
        Notre politique de sécurité évolue, nous vous demandons de redéfinir votre mot de passe afin de respecter nos nouvelles règles
        <br/>
        <br/>
        <Form form={form} onFinish={onUpdatePassword}>
          <Form.Item
            label="Votre mot de passe actuel"
            name="currentPassword"
            rules={[{ required: true, message: 'Mot de passe actuel' }]}
          >
            <Input.Password
              placeholder="Votre mot de passe actuel"
              size="middle"
              onChange={(e) => {
                const cleanedValue = e.target.value.replace(/\s+/g, '');
                form.setFieldsValue({ currentPassword: cleanedValue });
              }}
            />
          </Form.Item>
          <Form.Item
            label="Votre nouveau mot de passe"
            name="password"
            rules={[{
              required: true,
              message: 'Mon nouveau mot de passe',
              validator: (rule, value, callback) => {
                if (passwordIsValid(value)) return callback();
                callback('8 caractères dont au moins un caractère spécial et un chiffre');
              } 
            }]}
          >
            <Input.Password
              placeholder="Votre nouveau mot de passe"
              size="middle"
              onChange={(e) => {
                const cleanedValue = e.target.value.replace(/\s+/g, '');
                form.setFieldsValue({ password: cleanedValue });
              }}
            />
          </Form.Item>
          <Button
            size="large"
            htmlType="submit"
            disabled={hasErrors()}
          >
            Mettre à jour
          </Button>
          &nbsp;
          &nbsp;
          <button
            type="button"
            className="bg-transparent border-none p-0 cursor-pointer underline"
            onClick={onLostCurrentPassword}
          >
            {'J\'ai perdu mon mot de passe courant'}
          </button>
        </Form>
      </React.Fragment>
    );
  } else if (passwordIsUpdated) {
    return render(
      <React.Fragment>
        <Alert
          type="success"
          message="Votre mot de passe a bien été mis à jour"
        />
        <br/>
        <Button
          size="large"
          onClick={() => {
            window.location = '/';
          }}
        >
          Accéder au Menu
        </Button>
      </React.Fragment>
    );
  } else {
    return render(
      <React.Fragment>
        <Alert
          type="error"
          message={error === 'password_ko' ? 'Votre mot de passe n\'est pas correct' : 'Nous avons rencontré un problème'}
        />
        <br/>
        <Button
          size="large"
          onClick={onResetForm}
        >
          Recommencer
        </Button>
      </React.Fragment>
    );
  }
};

export default withUser(NewPasswordRequired);
